import axios from "axios";
import config from "../../constant";
import { addressKey, buildGeocodeQuery } from "../../addressGeo";

// Product lists are requested from several places on one page visit (Home
// before navigating, then the Products page). Share the in-flight request and
// reuse the response briefly so each visit downloads the list once. The window
// is short so merchant edits (sold out, price) still show up quickly.
const PRODUCT_CACHE_MS = 15 * 1000;
// A request still pending after this long is not shared any more (the generic
// `fetch` action never rejects, so a failed call would otherwise block retries).
const PRODUCT_PENDING_MS = 10 * 1000;
const productCache = new Map(); // key -> { startedAt, settledAt, request }

const cachedRequest = (key, send) => {
  const now = Date.now();
  const hit = productCache.get(key);
  const reusable =
    hit &&
    (hit.settledAt !== null
      ? now - hit.settledAt < PRODUCT_CACHE_MS
      : now - hit.startedAt < PRODUCT_PENDING_MS);

  let request;
  if (reusable) {
    request = hit.request;
  } else {
    const entry = { startedAt: now, settledAt: null, request: null };
    entry.request = send().then(
      (r) => {
        entry.settledAt = Date.now();
        return r;
      },
      (e) => {
        if (productCache.get(key) === entry) productCache.delete(key);
        throw e;
      }
    );
    productCache.set(key, entry);
    request = entry.request;
  }
  // Each caller gets its own copy: product objects are mutated later
  // (e.g. quantity when adding to cart) and must not leak into the cache.
  return request.then((r) => ({
    ...r,
    data: JSON.parse(JSON.stringify(r.data)),
  }));
};

// Geocoding lookups for this session, keyed by query (failures resolve to null)
const geocodeCache = new Map();

const addressComponent = (result, type) => {
  const c = result.address_components.find((x) => x.types.includes(type));
  return c ? c.long_name : "";
};

/**
 * Product & Tag Visibility Logic (GetOrders spec)
 * - hidden_tag: Tag excluded from GetOrders; products shown via search
 * - hidden_product/hidden_products: Products under tag excluded from GetOrders
 * - Time availability: Same-day window, inclusive (available_from <= current <= available_to)
 */

const isTagHidden = (tag) => tag.hidden_tag === true || tag.hidden_tag === 1;

const isProductHiddenUnderTag = (tag) =>
  tag.hidden_product === true ||
  tag.hidden_product === 1 ||
  tag.hidden_products === true ||
  tag.hidden_products === 1;

const isTagTimed = (tag) =>
  tag.available_from != null &&
  tag.available_to != null &&
  String(tag.available_from).trim() !== "" &&
  String(tag.available_to).trim() !== "";

/** Get current time in HH:mm:ss (24h) for comparison */
const getCurrentTimeString = () => {
  const now = new Date();
  return [
    String(now.getHours()).padStart(2, "0"),
    String(now.getMinutes()).padStart(2, "0"),
    String(now.getSeconds()).padStart(2, "0"),
  ].join(":");
};

/** Normalize time to HH:mm:ss for comparison (handles HH:mm or HH:mm:ss) */
const normalizeTime = (t) => {
  const s = String(t).trim();
  const parts = s.split(":");
  if (parts.length === 2)
    return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}:00`;
  return s;
};

/**
 * Get effective end time for comparison.
 * When available_to is "12:00" or "12:00:00", treat as end of that minute (12:00:59)
 * so product remains visible until 12:00:59 and disappears at 12:01:00.
 */
const getEffectiveEndTime = (to) => {
  const normalized = normalizeTime(to);
  const parts = normalized.split(":");
  const seconds = parseInt(parts[2] || "0", 10);
  if (seconds === 0) {
    const h = parts[0];
    const m = parts[1];
    return `${h}:${m}:59`;
  }
  return normalized;
};

/**
 * Same-day window: active if current_time >= available_from AND current_time <= available_to.
 * available_to is inclusive of the full minute (e.g. "12:00" = until 12:00:59, gone at 12:01).
 */
const isTagActiveNow = (tag, currentTime) => {
  const from = normalizeTime(tag.available_from);
  const to = getEffectiveEndTime(tag.available_to);
  return currentTime >= from && currentTime <= to;
};

/**
 * Product is in GetOrders if it has at least one visible tag (hidden_tag=0)
 * with hidden_product=0 for that tag.
 */
const hasVisibleTagForDisplay = (product) =>
  product.tags &&
  product.tags.some(
    (tag) => !isTagHidden(tag) && !isProductHiddenUnderTag(tag)
  );

/**
 * Product time availability:
 * - No timed tags among visible tags → always available
 * - At least one timed tag → available if ANY timed tag is active
 * - Non-timed tags ignored when timed tags exist
 */
const isProductAvailableByTime = (product, currentTime) => {
  if (!product.tags || product.tags.length === 0) return false;
  const visibleTags = product.tags.filter((t) => !isTagHidden(t));
  const timedTags = visibleTags.filter(isTagTimed);
  if (timedTags.length === 0) return true;
  return timedTags.some((tag) => isTagActiveNow(tag, currentTime));
};

/**
 * Filter products for GetOrders display:
 * - Has at least one visible tag with hidden_product=0
 * - Respects tag time availability
 */
const filterProductsForGetOrders = (products) => {
  const currentTime = getCurrentTimeString();
  return products.filter(
    (p) =>
      hasVisibleTagForDisplay(p) && isProductAvailableByTime(p, currentTime)
  );
};

const state = {
  user: {
    email: "",
    token:
      "eyJhbGciOiJSUzI1NiIsImtpZCI6IjRlOWRmNWE0ZjI4YWQwMjUwNjRkNjY1NTNiY2I5YjMzOTY4NWVmOTQiLCJ0eXAiOiJKV1QifQ.eyJpc3MiOiJodHRwczovL3NlY3VyZXRva2VuLmdvb2dsZS5jb20vbmV4dG9yZGVyLWF1dGgiLCJhdWQiOiJuZXh0b3JkZXItYXV0aCIsImF1dGhfdGltZSI6MTYxOTc2NTY1MCwidXNlcl9pZCI6IkJIVEFyeE8wbW5hMXlHMVh6OG9KQ0ZIVkxDcjEiLCJzdWIiOiJCSFRBcnhPMG1uYTF5RzFYejhvSkNGSFZMQ3IxIiwiaWF0IjoxNjE5NzY1NjUwLCJleHAiOjE2MTk3NjkyNTAsImVtYWlsIjoiYWRtaW5AZ2V0b3JkZXJzLmFwcCIsImVtYWlsX3ZlcmlmaWVkIjpmYWxzZSwiZmlyZWJhc2UiOnsiaWRlbnRpdGllcyI6eyJlbWFpbCI6WyJhZG1pbkBnZXRvcmRlcnMuYXBwIl19LCJzaWduX2luX3Byb3ZpZGVyIjoicGFzc3dvcmQifX0.B3PbLKWvg66_iQQiLmXMzGndTj0h6WtSNrfrBhBtjhJLJD7rgrBtZN-rabHdC3dOT15A-SeiykmOT89t-vKpj74U8k1SJIeZswVlU8QdU_otqnQAO_JzzluM2QIpP8T5uMvGTzqgQ4d-F9Adz0Wn-yBBN_E57qiAPexu0r5N9ln9H9viJqrezcMXgQppdBPuJoYLgipEnm7jeIVNMQYHOrHn__Sleu3qOoETAo0X2lo0HtFHL1RrSLeT4SHiwuqpECLIkCxmEcxKwbbvwRv3ZhN-lyBvyqk_KhAGY-dPRH-1I5lt7BkVZhJtAPASmNIH843J3RqtyPhqB3WeM3AtyA",
    uid: "",
  },
  users: [],
  order: [],
  selectedOutlet: null,
  selectedTableId: null,
  franchise: null,
  ident: null,
  loading: false,
  errorGeo: false,
  product: [],
  allProductsForSearch: [], // Includes products under hidden tags for search
  isFromSession: false,
  filtered_products: [],
  selectedTags: null,
  invoice_id: null,
  invoice: null,
  productReview: [],
  scroll: null,
  delivery: [
    {
      name: "Self Pickup",
      provider: null,
      data: null,
    },
  ],
  // BetterPay Dialog States
  showBetterPayDialog: false,
  betterPayData: null,
  betterPayProcessing: false,
  betterPayType: null, // 'delivery' or 'checkout'
  // Inventory States
  inventory: [],
  inventoryLoaded: false,
};

const getters = {
  getLoading: (state) => state.loading,
  getFranchise: (state) => state.franchise,
  getSelectedTableId: (state) => state.selectedTableId,
  getProduct: (state) => state.product,
  getFilteredProducts: (state) => state.filtered_products,
  getAllProductsForSearch: (state) =>
    state.allProductsForSearch?.length > 0
      ? state.allProductsForSearch
      : state.product,
  getSelectedTags: (state) => state.selectedTags,
  getUsers: (state) => state.users,
  getInvoice: (state) => state.invoice,
  getScroll: (state) => state.scroll,
  getProductReview: (state) => state.productReview,
  getOrder: (state) => state.order,
  getSelectedOutlet: (state) => state.selectedOutlet,
  // BetterPay Dialog Getters
  getBetterPayDialog: (state) => state.showBetterPayDialog,
  getBetterPayProcessing: (state) => state.betterPayProcessing,
  getBetterPayType: (state) => state.betterPayType,
  // Inventory Getters
  getInventory: (state) => state.inventory,
  getInventoryLoaded: (state) => state.inventoryLoaded,
  /** Tag is currently active: non-timed = always active, timed = within available_from..available_to */
  isTagCurrentlyActive: () => (tag) => {
    if (!isTagTimed(tag)) return true;
    const currentTime = getCurrentTimeString();
    return isTagActiveNow(tag, currentTime);
  },
  getAuthHeader: (state) => {
    const item = {
      Accept: "application/json",
      "Content-Type": "application/json",
      "x-api-key": "9R03ClUKfaRHP6lkvg4349ZCfWd1XHP3rjQCk023",
      Authorization: state.user.token,
    };
    return item;
  },
};

const actions = {
  startLoad({ state }) {
    state.loading = true;
  },
  updateIsFromSession({ state }, value) {
    state.isFromSession = value;
  },
  handleResetFilterProduct({ state }) {
    state.filtered_products = state.product || [];
    state.selectedTags = null;
  },
  handleSearchProductByName({ state }, value) {
    const keyword = value.toLowerCase();
    // Search in display products only: respect hidden_tag, hidden_product, and tag time availability
    const searchSource = state.product;
    if (!searchSource || !Array.isArray(searchSource)) {
      state.filtered_products = [];
      return;
    }
    state.filtered_products = searchSource.filter((product) => {
      const name = product.name?.toLowerCase() || "";
      const code = product.code?.toLowerCase() || "";
      return name.includes(keyword) || code.includes(keyword);
    });
  },
  handleFilterProductByTag({ state, getters }, value) {
    if (!state.product || !Array.isArray(state.product)) {
      state.filtered_products = [];
      state.selectedTags = value?.name;
      return;
    }
    const isTagActive = getters.isTagCurrentlyActive;
    if (!isTagActive || !isTagActive(value)) {
      state.filtered_products = [];
      state.selectedTags = value?.name;
      return;
    }
    // Product must belong to tag AND tag must have hidden_product=0 for that tag
    let filterProductByTag = state.product.filter(
      (product) =>
        product.tags &&
        product.tags.some(
          (tag) =>
            tag.name.toLowerCase() === value.name.toLowerCase() &&
            !isProductHiddenUnderTag(tag)
        )
    );
    state.filtered_products = filterProductByTag;
    state.selectedTags = value.name;
  },
  endLoad({ state }) {
    state.loading = false;
  },
  updateSO({ state }, value) {
    state.selectedOutlet = value;
  },
  setSelectedTableId({ state }, value) {
    state.selectedTableId = value;
  },
  changeScroll({ state }, value) {
    state.scroll = value;
  },

  fetch({ state }, value) {
    return new Promise((res) => {
      const item = {
        method: value.method,
        url: `https://api.getorders.app${value.url}`,
        data: { ...value.data },
        headers: {},
      };
      if (value.headers) {
        item.headers = { ...value.headers };
      } else {
        item.headers = {
          Code: state.ident || "",
          fire_id: state.franchise ? state.franchise.fire_id : "",
          Accept: "application/json",
          "Content-Type": "application/json",
          "x-api-key": "EF95vGTnuo1GDqSQw6BmN41IR1G5R0Li8E5WIxlm",
        };
        const _table_id = state.selectedTableId || "";
        if (value.url === "/guest/franchise" && _table_id) {
          item.headers.table_selected = state.selectedTableId || "";
        }
        if (state.franchise && state.franchise.acc_id) {
          item.headers.acc_id = state.franchise.acc_id;
        }
        if (state.franchise && state.franchise.f_id) {
          item.headers.fran_id = state.franchise.f_id;
        }
        if (state.invoice_id) {
          item.headers.order_id = state.invoice_id;
        }
        if (state.selectedOutlet && state.selectedOutlet.value) {
          item.headers.outlet_id = state.selectedOutlet.value;
        }
        if (state.isFromSession) {
          item.headers.type = "session";
        }
      }
      axios(item).then((d) => {
        if (d.data.status == "failed" && d.data.message == "unauthorize") {
          alert("error");
        } else {
          res(d);
        }
      });
    });
  },

  fetchFranchise({ state, dispatch }, value) {
    return new Promise((res) => {
      state.loading = true;
      state.ident = value.code;
      state.selectedTableId = value.table_id;
      dispatch("fetch", { url: "/guest/franchise", method: "get" }).then(
        (r) => {
          if (r.data.error) {
            res({ error: true, msg: "Invalid Merchant" });
          }
          const expirationKeywords = ["session expired", "expired"];
          const isExpired = expirationKeywords.some(
            (keyword) =>
              r.data.message && r.data.message.toLowerCase().includes(keyword)
          );
          if (isExpired) {
            res({ is_expired: true, message: r.data.message });
          }
          state.franchise = r.data;
          const dataOrder = localStorage.getItem("order");
          if (dataOrder)
            state.order = JSON.parse(dataOrder).filter(
              (d) => d.fire_id == state.franchise.fire_id
            );
          state.loading = false;
          res(r.data);
        }
      );
    });
  },

  fetchOutlet({ state }, outletId) {
    return new Promise((res) => {
      const item = {
        method: "get",
        url: `https://db.getorders.app/products.sl.${state.franchise.f_id}.${outletId}.json`,
        headers: {},
      };
      item.headers = {
        Code: state.ident || "",
        fire_id: state.franchise ? state.franchise.fire_id : "",
        Accept: "application/json",
        "Content-Type": "application/json",
        "x-api-key": "EF95vGTnuo1GDqSQw6BmN41IR1G5R0Li8E5WIxlm",
      };
      if (state.invoice_id) {
        item.headers.order_id = state.invoice_id;
      }
      cachedRequest(`${item.url}|${item.headers.Code}`, () => axios(item)).then(
        (d) => {
          res(d);
        }
      );
    });
  },

  fetchProduct({ state, dispatch }, outletId) {
    return new Promise((res) => {
      // Initialize with empty arrays to prevent forEach errors
      state.product = [];
      state.filtered_products = [];

      if (state.franchise.outlets.length > 0 || outletId) {
        dispatch("fetchOutlet", outletId).then((r) => {
          let a = r.data.filter((d) =>
            d.media_urls.length > 0 && d.tags.length > 0 ? true : false
          );
          let inclusive = r.data.map((d) => d.taxes).filter((d) => d.inclusive);
          if (inclusive.length > 0) {
            // code here
          }
          a = a.filter((d) => (d.tags.length > 0 ? true : false));

          // Apply inventory filtering
          dispatch("processProductsWithInventory", {
            products: a,
            outletId: outletId,
          }).then((filteredProducts) => {
            // Store all products for search (includes products under hidden tags)
            state.allProductsForSearch = filteredProducts;

            // Apply Product & Tag Visibility: hidden_tag, hidden_product, time availability
            const displayProducts =
              filterProductsForGetOrders(filteredProducts);

            // Sort products according to requirements:
            // 1. Products with code come first, sorted by product_code
            // 2. Products without code sorted by product_name
            const sortedProducts = displayProducts.sort((a, b) => {
              const aHasCode = a.code && a.code.trim() !== "";
              const bHasCode = b.code && b.code.trim() !== "";

              // If both have codes, sort by code (numeric-aware)
              if (aHasCode && bHasCode) {
                return a.code.localeCompare(b.code, undefined, {
                  numeric: true,
                  sensitivity: "base",
                });
              }

              // If only a has code, a comes first
              if (aHasCode && !bHasCode) {
                return -1;
              }

              // If only b has code, b comes first
              if (!aHasCode && bHasCode) {
                return 1;
              }

              // If neither has code, sort by name
              const aName = a.name || "";
              const bName = b.name || "";
              return aName.localeCompare(bName, undefined, {
                sensitivity: "base",
              });
            });

            state.filtered_products = sortedProducts;
            state.product = sortedProducts;
            state.loading = false;
            res(sortedProducts);
          });
        });
      }
      // Key covers every header `fetch` sends that can change the list
      else
        cachedRequest(
          [
            "/guest/products",
            state.ident,
            state.franchise && state.franchise.fire_id,
            state.selectedOutlet && state.selectedOutlet.value,
            state.isFromSession,
            state.invoice_id,
          ].join("|"),
          () =>
            dispatch("fetch", {
              url: "/guest/products?test=true",
              method: "get",
            })
        ).then((r) => {
          var a = r.data;

          // Apply inventory filtering for shoplink (no outlet)
          dispatch("processProductsWithInventory", {
            products: a,
            outletId: null,
          }).then((filteredProducts) => {
            state.allProductsForSearch = filteredProducts;
            const displayProducts =
              filterProductsForGetOrders(filteredProducts);
            state.product = displayProducts.sort((a, b) =>
              (a.tags[0]?.name || "").localeCompare(b.tags[0]?.name || "")
            );
            state.filtered_products = state.product;
            state.loading = false;
            res(state.product);
          });
        });
    });
  },

  fetchUsers({ state }) {
    let i = localStorage.getItem("user");
    if (i == null) {
      state.users = [];
    } else {
      state.users = JSON.parse(i);
    }
  },

  saveUser({ state }, value) {
    if (value.status === null) {
      state.users.push(value.user);
      let a = state.users.map((d) => {
        if (d.selected == undefined) {
          d.selected = true;
          return { ...d };
        } else {
          d.selected = false;
          return { ...d };
        }
      });
      state.users = a;
      localStorage.setItem("user", JSON.stringify(state.users));
    } else {
      const updatedUsers = [...state.users];
      updatedUsers[value.status] = {
        ...value.user,
        selected: state.users[value.status].selected,
      };
      state.users = updatedUsers;
      localStorage.setItem("user", JSON.stringify(state.users));
    }
  },

  saveOrder({ state }, data) {
    const dataOrder = localStorage.getItem("order");
    var old_order = [];
    if (dataOrder) {
      old_order = JSON.parse(dataOrder);
    }
    old_order.push(data);
    localStorage.setItem("order", JSON.stringify(old_order));
    state.order = old_order;
  },

  resetOrder({ state }) {
    state.order = [];
  },

  insertUser({ state }, value) {
    value.selected = true;
    if (value.email && value.email.includes("%40")) {
      value.email.replace("%40", "@");
      state.users = [value];
    } else state.users = [value];
  },

  setUser({ state }, value) {
    let a = state.users.map((d, i) => {
      if (i == value) {
        d.selected = true;
        return { ...d };
      } else {
        d.selected = false;
        return { ...d };
      }
    });
    state.users = a;
    localStorage.setItem("user", JSON.stringify(state.users));
  },

  changeOutlet({ state, dispatch }, value) {
    return new Promise((res) => {
      // Initialize with empty arrays to prevent forEach errors
      state.product = [];
      state.filtered_products = [];

      dispatch("fetchOutlet", value).then((r) => {
        let a = r.data.filter((d) => (d.media_urls.length > 0 ? true : false));

        // Apply inventory filtering
        dispatch("processProductsWithInventory", {
          products: a,
          outletId: value,
        }).then((filteredProducts) => {
          state.allProductsForSearch = filteredProducts;
          const displayProducts = filterProductsForGetOrders(filteredProducts);
          state.product = displayProducts.sort((a, b) =>
            (a.tags[0]?.name || "").localeCompare(b.tags[0]?.name || "")
          );
          state.filtered_products = state.product;
          state.loading = false;
          res(state.product);
        });
      });
    });
  },

  fetchInvoice({ state, dispatch }, value) {
    state.invoice_id = value;
    state.invoice = null;
    dispatch("fetch", { url: "/guest/invoice", method: "get" }).then((r) => {
      state.invoice = r.data;
      state.loading = false;
    });
  },

  // Returns { address, address2, city, state, latitude, longitude } or null.
  // Never throws and never hangs. Google's answers (failures too) are cached
  // per session, and concurrent lookups of one query share a single request.
  fetchGeocode({ state }, value) {
    const query = buildGeocodeQuery(value);
    if (query.length < 8) return Promise.resolve(null);
    if (geocodeCache.has(query)) return geocodeCache.get(query);

    state.loading = true;
    const request = axios
      .get("https://maps.googleapis.com/maps/api/geocode/json", {
        params: {
          address: query,
          components: "country:MY",
          region: "my",
          key: config.Google_API,
        },
      })
      .then((r) => {
        const found = r.data && r.data.status === "OK" && r.data.results[0];
        if (!found) return null;
        return {
          address: value.address,
          address2: value.address2,
          city:
            addressComponent(found, "locality") ||
            addressComponent(found, "administrative_area_level_2"),
          state: addressComponent(found, "administrative_area_level_1"),
          latitude: found.geometry.location.lat,
          longitude: found.geometry.location.lng,
        };
      })
      .catch((e) => {
        // 4xx/5xx answers from Google are billed like any other: keep them
        // cached. Network errors never reached Google, so allow a retry.
        if (!e.response) geocodeCache.delete(query);
        return null;
      })
      .finally(() => {
        state.loading = false;
      });
    geocodeCache.set(query, request);
    return request;
  },

  // Customer coordinates: saved on the address, else one lookup that is
  // saved back to the address so the next order needs no lookup.
  async resolveCustomerCoords({ state, dispatch }, cust) {
    if (!cust) return null;
    const hasPin = cust.lat != null && cust.lng != null;
    if (hasPin && cust.city && cust.state) {
      return {
        address: cust.address,
        address2: cust.address2,
        city: cust.city,
        state: cust.state,
        latitude: cust.lat,
        longitude: cust.lng,
      };
    }

    const geo = await dispatch("fetchGeocode", {
      address: cust.address,
      address2: cust.address2,
      postcode: cust.postcode,
    });
    if (!geo) return null;

    // A pin the customer placed on the map is more precise than the lookup
    const coords = {
      ...geo,
      latitude: hasPin ? cust.lat : geo.latitude,
      longitude: hasPin ? cust.lng : geo.longitude,
    };

    let idx = state.users.indexOf(cust);
    if (idx === -1) {
      idx = state.users.findIndex(
        (u) => u.name === cust.name && addressKey(u) === addressKey(cust)
      );
    }
    if (idx !== -1) {
      await dispatch("saveUser", {
        user: {
          ...state.users[idx],
          lat: coords.latitude,
          lng: coords.longitude,
          city: coords.city,
          state: coords.state,
        },
        status: idx,
      });
    }
    return coords;
  },

  uuid() {
    return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
      (
        c ^
        (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
      ).toString(16)
    );
  },

  generate({ state }, value) {
    var url = "";
    url = `https://api.whatsapp.com/send?phone=${state.franchise.phone}&text=`;
    let cust = "";
    if (value.delivery == true) {
      cust = `*${value.customer.name}*\n${value.customer.address}\n${value.customer.address2}\n${value.customer.postcode}`;
    } else {
      cust = `*${value.customer.name}*`;
    }
    if (value.notes != "") cust += `\n\nNotes : ${value.notes}`;
    var list = `*${
      value.delivery == true ? "DELIVERY" : "PICKUP"
    } from getorders.app*\n\n`;
    var msg = "To check invoice, tap here";
    list += `${state.franchise.outlet_name || state.franchise.name}\n\n`;
    list += `${msg} 👉🏻 https://v2.getorders.app/i/${value.invoice_id}\n\n`;
    value.cart.forEach((p) => {
      list += `*${p.name} x${p.quantity}*\n`;
      if (p.variant) {
        p.variant.forEach((v) => {
          list += ` _- ${v.name}_\n`;
        });
      }
      p.modifiers.forEach((m) => {
        list += ` _- ${m.name}_\n`;
      });
      if (p.remarks) {
        list += `_Remarks : ${p.remarks}_\n`;
      }
    });
    var txt = encodeURIComponent(list + "\n" + cust);
    return `${url}${txt}`;
  },

  pay_at_counter({ dispatch, rootGetters }, value) {
    let v = rootGetters["getVoucher"];
    if (v) {
      value.voucher = v.id;
    }
    return dispatch("fetch", {
      url: "/guest/checkout",
      method: "post",
      data: value,
    }).then((r) => r.data);
  },

  checkout({ dispatch, rootGetters }, value) {
    let v = rootGetters["getVoucher"];
    if (v) {
      value.voucher = v.id;
    }
    dispatch("fetch", {
      url: "/guest/delivery",
      method: "post",
      data: value,
    }).then(async (r) => {
      value.invoice_id = r.data.invoice_id;
      if (r.data.checkoutURI) {
        document.location.href = r.data.checkoutURI;
      } else document.location.href = await dispatch("generate", value);
    });
  },

  // Modified BetterPay Actions with Dialog Integration
  payBetterPay({ dispatch, commit, rootGetters }, value) {
    let v = rootGetters["getVoucher"];
    if (v) {
      value.voucher = v.id;
    }

    return dispatch("fetch", {
      url: "/guest/delivery",
      method: "post",
      data: value,
    })
      .then(async (r) => {
        if (r.data.params) {
          // Calculate total amount for display
          const totalAmount =
            value.cart?.reduce((total, item) => {
              return total + item.price * item.quantity;
            }, 0) || 0;

          // Store the response data and show confirmation dialog
          commit("SET_BETTERPAY_DATA", {
            params: r.data.params,
            endpoint: r.data.endpoint,
            originalValue: value,
            orderSummary: {
              total: totalAmount,
              voucher: v ? v.code : null,
              items: value.cart || [],
              delivery: value.delivery || false,
              customer: value.customer || null,
            },
          });
          commit("SET_BETTERPAY_TYPE", "delivery");
          commit("SET_BETTERPAY_DIALOG", true);
        }
      })
      .catch((error) => {
        console.error("BetterPay payment error:", error);
        // You can add error handling here
      });
  },

  payBetterPayCheckout({ dispatch, commit, rootGetters }, value) {
    let v = rootGetters["getVoucher"];
    if (v) {
      value.voucher = v.id;
    }

    return dispatch("fetch", {
      url: "/guest/checkout",
      method: "post",
      data: value,
    })
      .then(async (r) => {
        if (r.data.payment_gateway) {
          // Calculate total amount for display
          const totalAmount =
            value.cart?.reduce((total, item) => {
              return total + item.price * item.quantity;
            }, 0) || 0;

          // Store the response data and show confirmation dialog
          commit("SET_BETTERPAY_DATA", {
            params: r.data.payment_gateway.params,
            endpoint: r.data.payment_gateway.endpoint,
            originalValue: value,
            orderSummary: {
              total: totalAmount,
              voucher: v ? v.code : null,
              items: value.cart || [],
              delivery: false, // checkout is typically for pickup
              customer: value.customer || null,
            },
          });
          commit("SET_BETTERPAY_TYPE", "checkout");
          commit("SET_BETTERPAY_DIALOG", true);
        }
      })
      .catch((error) => {
        console.error("BetterPay checkout error:", error);
        // You can add error handling here
      });
  },

  // New action to handle the actual redirect after confirmation
  proceedToBetterPay({ commit, state }) {
    commit("SET_BETTERPAY_PROCESSING", true);

    const { params, endpoint } = state.betterPayData;

    // Add small delay for better UX
    setTimeout(() => {
      const createFormField = (name, value) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = name;
        input.value = value;
        return input;
      };

      const urlParams = new URLSearchParams(params);
      const form = document.createElement("form");
      form.method = "POST";
      form.action = endpoint;

      for (const [key, value] of urlParams) {
        form.appendChild(createFormField(key, value));
      }

      document.body.appendChild(form);
      form.submit();

      // Reset state after redirect
      commit("SET_BETTERPAY_DIALOG", false);
      commit("SET_BETTERPAY_DATA", null);
      commit("SET_BETTERPAY_PROCESSING", false);
      commit("SET_BETTERPAY_TYPE", null);
    }, 1500); // 1.5 second delay for better UX
  },

  // Action to cancel BetterPay
  cancelBetterPay({ commit }) {
    commit("SET_BETTERPAY_DIALOG", false);
    commit("SET_BETTERPAY_DATA", null);
    commit("SET_BETTERPAY_PROCESSING", false);
    commit("SET_BETTERPAY_TYPE", null);
  },

  saveReview({ dispatch }, value) {
    dispatch("fetch", {
      url: "/guest/review",
      method: "post",
      data: value,
    }).then((r) => {
      console.log(r);
    });
  },

  saveProductsReview({ dispatch }, value) {
    dispatch("fetch", {
      url: "/guest/product_review",
      method: "post",
      data: value,
    }).then((r) => {
      console.log(r);
    });
  },

  fetchReview({ state, dispatch }, value) {
    dispatch("fetch", {
      url: `/guest/product_review/${value}`,
      method: "get",
    }).then((r) => {
      state.productReview = r.data;
      state.loading = false;
    });
  },

  // Fetch inventory data
  fetchInventory({ state, dispatch }) {
    return new Promise((res) => {
      const headers = {
        Code: state.ident || "",
        Accept: "application/json",
        "Content-Type": "application/json",
        "x-api-key": "EF95vGTnuo1GDqSQw6BmN41IR1G5R0Li8E5WIxlm",
      };

      // Add session type if applicable
      if (state.isFromSession) {
        headers.type = "session";
      }

      // Add franchise and account IDs
      if (state.franchise && state.franchise.fire_id) {
        headers.fire_id = state.franchise.fire_id;
      }
      if (state.franchise && state.franchise.acc_id) {
        headers.acc_id = state.franchise.acc_id;
      }
      if (state.franchise && state.franchise.f_id) {
        headers.fran_id = state.franchise.f_id;
      }

      // Add outlet_id if available
      if (state.selectedOutlet && state.selectedOutlet.value) {
        headers.outlet_id = state.selectedOutlet.value;
      } else if (state.franchise && state.franchise.outlet_id) {
        headers.outlet_id = state.franchise.outlet_id;
      }

      dispatch("fetch", {
        url: "/guest/inventory",
        method: "get",
        headers: headers,
      })
        .then((r) => {
          state.inventory = r.data;
          state.inventoryLoaded = true;
          res(r.data);
        })
        .catch((error) => {
          console.error("❌ Error fetching inventory:", error);
          state.inventory = [];
          state.inventoryLoaded = false;
          res([]);
        });
    });
  },

  // Process products with inventory filtering
  processProductsWithInventory({ state, dispatch }, { products, outletId }) {
    return new Promise((res) => {
      // If inventory is not loaded, fetch it first
      if (!state.inventoryLoaded) {
        dispatch("fetchInventory").then(() => {
          const filteredProducts = dispatch("filterProductsByInventory", {
            products,
            outletId,
          });
          res(filteredProducts);
        });
      } else {
        const filteredProducts = dispatch("filterProductsByInventory", {
          products,
          outletId,
        });
        res(filteredProducts);
      }
    });
  },

  // Filter products based on soldAt (sales channel filtering)
  filterProductsByInventory({ state }, { products, outletId }) {
    if (!state.inventory || state.inventory.length === 0) {
      return products;
    }

    // Determine filter based on session type
    // 0: Takeaway/shoplink, 1: Delivery/shoplink, 2: Dine-in/QR session/static
    let filter = [];

    // Check if this is a QR session or static (dine-in)
    const isDineIn =
      (state.franchise && state.franchise.table_name) ||
      state.isFromSession ||
      state.selectedTableId;

    if (isDineIn) {
      // This is QR session or static - show only dine-in products (soldAt: 2)
      filter = [2];
    } else {
      // This is shoplink - show takeaway and delivery products (soldAt: 0, 1)
      filter = [0, 1];
    }

    const filteredProducts = [];

    products.forEach((product) => {
      // Find inventory entries for this product and outlet
      let inventoryEntries = [];

      if (outletId) {
        // For outlets, filter by outlet_id
        inventoryEntries = state.inventory.filter(
          (inv) =>
            inv.product_id === product.id.toString() &&
            parseInt(inv.outlet_id) === parseInt(outletId)
        );
      } else {
        // For shoplink (no outlet), get all inventory entries for this product
        inventoryEntries = state.inventory.filter(
          (inv) => inv.product_id === product.id.toString()
        );
      }

      if (inventoryEntries.length > 0) {
        // Check if any inventory entry matches soldAt filter
        const canShowProduct = inventoryEntries.some((inv) => {
          // If soldAt is empty array, hide the product completely
          if (!inv.soldAt || inv.soldAt.length === 0) {
            return false;
          }

          // Check if soldAt array contains any of our filter values
          const hasMatchingSoldAt =
            inv.soldAt.filter((soldAt) => filter.includes(soldAt)).length > 0;

          return hasMatchingSoldAt;
        });

        if (canShowProduct) {
          // Filter variants if needed
          if (product.variants && product.variants.length > 0) {
            const filteredVariants = product.variants.filter((variant) => {
              const variantInventory = inventoryEntries.find(
                (inv) => inv.variant_id === variant.id.toString()
              );

              if (variantInventory) {
                // If soldAt is empty array, hide the variant completely
                if (
                  !variantInventory.soldAt ||
                  variantInventory.soldAt.length === 0
                ) {
                  return false;
                }

                // If variant has inventory, check if it matches soldAt filter
                const hasMatchingSoldAt =
                  variantInventory.soldAt.filter((soldAt) =>
                    filter.includes(soldAt)
                  ).length > 0;
                return hasMatchingSoldAt;
              }

              // If no specific inventory for variant, check product-level inventory
              const productInventory = inventoryEntries.find(
                (inv) => inv.variant_id === ""
              );
              if (productInventory) {
                // If soldAt is empty array, hide the variant completely
                if (
                  !productInventory.soldAt ||
                  productInventory.soldAt.length === 0
                ) {
                  return false;
                }

                const hasMatchingSoldAt =
                  productInventory.soldAt.filter((soldAt) =>
                    filter.includes(soldAt)
                  ).length > 0;
                return hasMatchingSoldAt;
              }

              return true; // Show variant if no inventory restrictions
            });

            product.variants = filteredVariants;
          }

          // Only add product if it has tags (following existing logic)
          if (product.tags && product.tags.length > 0) {
            filteredProducts.push(product);
          }
        }
      } else {
        // No inventory restrictions for this product, show it if it has tags
        if (product.tags && product.tags.length > 0) {
          filteredProducts.push(product);
        }
      }
    });
    return filteredProducts;
  },

  // Update selected product
  updateSP({ state }, product) {
    state.product = product;
  },
};

const mutations = {
  // BetterPay Dialog Mutations
  SET_BETTERPAY_DIALOG(state, show) {
    state.showBetterPayDialog = show;
  },
  SET_BETTERPAY_DATA(state, data) {
    state.betterPayData = data;
  },
  SET_BETTERPAY_PROCESSING(state, processing) {
    state.betterPayProcessing = processing;
  },
  SET_BETTERPAY_TYPE(state, type) {
    state.betterPayType = type;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
